import { EnvironmentUtils } from "@rsm-hcd/javascript-core";
import Button, { ButtonTypes } from "atoms/buttons/button";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import FileRecord from "models/view-models/file-record";
import { ListBoxItemClassName } from "molecules/lists/list-box";
import FileUploadProgressBar from "molecules/progress-bars/file-upload-progress-bar";
import React, { useCallback, useRef, useState } from "react";
import { S3Response } from "react-s3-uploader";
import { CollectionUtils } from "utilities/collection-utils";
import { CoreUtils } from "utilities/core-utils";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import FileUtils from "utilities/file-utils";
import { t } from "utilities/localization-utils";
import RemoteAccessDetailsService from "utilities/services/remote-access-details-service";
import StringUtils from "utilities/string-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import uuid from "uuid";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface ImageUploadFieldProps {
    uploadButtonText: string;
    accept?: string | Array<string>;
    /**
     * Return false to abort the upload to S3.
     * This callback gives you an opportunity to perform
     * validation on the file before uploading it.
     * @param file
     */
    beforeUpload?: (file: File) => Promise<boolean>;
    /**
     * Customize the confirm button text in the delete confirmation modal
     * @default "Yes, Remove"
     */
    confirmButtonText?: string;
    /**
     * Customize the text in the delete confirmation modal
     * @default "Are you sure you want to remove this file?"
     */
    confirmModalText?: string;
    /**
     * Show a confirmation dialog before calling onFileChanged(undefined)
     * @default false
     */
    confirmRemove?: boolean;
    /**
     * If true, will console.log updates about the upload progress,
     * only if in the development environment.
     */
    debugS3UploadProgress?: boolean;
    /**
     * Allows you to show loading file deletion state
     * when deleted from outside this component, for example
     * when closing the solution resource modal with the
     * cancel button.
     */
    disabled?: boolean;
    errorMessage?: string;
    errorMessages?: Array<string>;
    fileUploadDestination?: FileUploadDestination;
    /**
     * Text to render under the "attach file" label.
     * You can use this to display instructions such as
     * a maximum file size or maximum image dimensions.
     */
    helpText?: string;
    isErrored?: boolean;
    /**
     * Max file size to accept, in Bytes.
     * You can use FileUtils#convertMbToBytes
     * if you want to specify size in MB
     */
    maxSize?: number;
    onFileChanged?: (file?: FileRecord) => void;
    onRetryUpload?: () => void;
    onUploadError?: (message: string) => void;
    onUploadStart?: () => void;
    required?: boolean;
    /**
     * Used, for example, to put the publication ID in the relative path.
     */
    uploadPathPrefix?: string;
    value?: FileRecord;
    userUploadId: string;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const CSS_BASE_CLASS = "c-image-upload-field";

const ImageUploadField: React.FC<ImageUploadFieldProps> = (
    props: ImageUploadFieldProps
) => {
    const {
        beforeUpload,
        debugS3UploadProgress,
        fileUploadDestination,
        isErrored,
        maxSize,
        onFileChanged,
        onRetryUpload,
        onUploadError,
        onUploadStart,
        uploadPathPrefix,
        value,
        helpText,
        errorMessage,
        errorMessages,
        userUploadId,
        uploadButtonText,
    } = props;

    const cssClassNames = [CSS_BASE_CLASS, "c-form-field"];

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [inputFile, setInputFile] = useState<File | undefined>();
    const [progress, setProgress] = useState<number>(0);
    const uploadFileRef = useRef<HTMLInputElement>(null);
    if (isErrored) {
        cssClassNames.push("-error");
    }

    const onFinishUploading = useCallback(
        async (uploadResult: S3Response, file?: FileRecord) => {
            if (!isErrored) {
                // show the success state before transitioning
                await CoreUtils.sleep(500);
            }

            if (value != null) {
                FileUtils.deleteFile(value);
            }
            setInputFile(undefined);
            setProgress(0);

            if (file == null) {
                onFileChanged?.(file);
                return;
            }

            // Presign the url with the GET verb for caching
            const remoteAccessDetailsResult =
                await RemoteAccessDetailsService.get(undefined, {
                    storageContainer: file.storageContainer,
                    relativeProviderPath: file.relativeProviderPath,
                });

            const uploadedFile = file.with({
                presignedUrl: remoteAccessDetailsResult.resultObject?.url,
                suggestedCode: file.suggestedCode,
                suggestedEdition: file.suggestedEdition,
                suggestedLocale: file.suggestedLocale,
            });

            onFileChanged?.(uploadedFile);
            //Keep showing the preview image until the save is complete.
            setPreviewUrl(null);
        },
        [isErrored, value, onFileChanged]
    );

    const handleUploadError = useCallback(
        (message: string) => {
            ToastManager.error(message);
            onUploadError?.(message);
        },
        [onUploadError]
    );

    const handleProgress = useCallback(
        (percent: number, status: string, file: File) => {
            if (debugS3UploadProgress === true) {
                EnvironmentUtils.runIfDevelopment(() =>
                    console.log(
                        `S3-UPLOAD:: ${file.name}: ${percent}% - ${status}`
                    )
                );
            }
            setProgress(percent);
        },
        [debugS3UploadProgress]
    );

    const handleRetry = () => {
        if (inputFile != null) {
            uploadFile(inputFile);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files != null && e.target.files.length > 0) {
            uploadFile(e.target.files[0]);
        }
    };

    const handleUploadStart = useCallback(
        async (file: File, next?: (file: File) => any): Promise<boolean> => {
            setInputFile(file);
            setProgress(0);
            next?.(file);

            if (beforeUpload != null) {
                const propValidateResult = await beforeUpload(file);
                if (!propValidateResult) {
                    return false;
                }
            }

            if (maxSize != null && file.size > maxSize) {
                ToastManager.error(
                    t("file-upload-file-too-large", {
                        size: FileUtils.bytesToMegabytes(maxSize),
                    })
                );
                return false;
            }

            onUploadStart?.();
            return true;
        },
        [beforeUpload, maxSize, onUploadStart]
    );

    const handleUploadAbort = useCallback(() => {
        setProgress(0);
        setInputFile(undefined);
        onFileChanged?.(undefined);
    }, [onFileChanged]);

    const uploadFile = useCallback(
        (file: File) => {
            onRetryUpload?.();
            FileUtils.uploadFile(file, {
                beforeUpload: handleUploadStart,
                fileUploadDestination,
                onAbort: handleUploadAbort,
                onError: handleUploadError,
                onFinish: onFinishUploading,
                onProgress: handleProgress,
                uploadPathPrefix,
                bucketPrefix: userUploadId,
            });
        },
        [
            fileUploadDestination,
            handleProgress,
            handleUploadAbort,
            handleUploadError,
            handleUploadStart,
            onFinishUploading,
            onRetryUpload,
            uploadPathPrefix,
            userUploadId,
        ]
    );

    const getFileContainerClassNames = (): string => {
        const classNames = [
            ListBoxItemClassName,
            `${CSS_BASE_CLASS}__file-container__progress`,
        ];
        if (progress >= 100 && !isErrored) {
            classNames.push("-success");
        }

        if (isErrored) {
            classNames.push("-error");
        }

        return classNames.join(" ");
    };

    return (
        <div className={CSS_BASE_CLASS}>
            {value == null && inputFile == null && previewUrl == null && (
                <>
                    <Button type={ButtonTypes.Button}>
                        <Icon
                            type={Icons.ImageUpload}
                            size={IconSizes.Medium}></Icon>
                        <label
                            htmlFor="file-upload-field"
                            className="upload-image-button-text">
                            {uploadButtonText ?? t("file-upload-upload-image")}
                        </label>
                        <input
                            type="file"
                            id="file-upload-field"
                            ref={uploadFileRef}
                            onChange={handleFileInputChange}
                            style={{ display: "none" }}
                            tabIndex={-1}
                        />
                    </Button>
                    <span className="-optional">*{t("optional")}</span>
                    <span className="-help-text">{helpText}</span>
                </>
            )}
            {(value != null || previewUrl != null) && (
                <React.Fragment>
                    {
                        // if
                        value == null && inputFile != null && (
                            <div className={getFileContainerClassNames()}>
                                <FileUploadProgressBar
                                    cssClassName={`${CSS_BASE_CLASS}__file-container__progress__progress-bar`}
                                    isErrored={isErrored}
                                    onRetryClick={handleRetry}
                                    value={progress}
                                />
                            </div>
                        )
                    }
                </React.Fragment>
            )}
            {
                // if
                (CollectionUtils.hasValues(errorMessages) ||
                    StringUtils.hasValue(errorMessage)) && (
                    <div
                        className={`${CSS_BASE_CLASS}__errors-container c-form-field__bottom__errors`}>
                        {errorMessages?.map((s: string) => (
                            <label key={uuid.v4()}>{s}</label>
                        ))}
                        {StringUtils.hasValue(errorMessage) && (
                            <label>{errorMessage}</label>
                        )}
                    </div>
                )
            }
        </div>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default ImageUploadField;

// #endregion Exports
